/**
 * Payor Detail / Create page (US-028).
 *
 * `/payors/new`     → create form
 * `/payors/<id>`    → view + edit form, re-validate button
 *
 * Affiliation classification is the inline question Mike highlighted —
 * default "Not sure" so no athlete is blocked guessing. Validation runs
 * server-side on create; on edit, fields update but validation is not
 * automatically re-run — the athlete clicks "Re-validate".
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  ValidationBadge,
  type ValidationBadgeStatus,
} from '@app/ui';
import { PageTransition } from '@app/ui/motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { RouteErrorComponent } from '../../../components/route-error';
import { api, client } from '../../../lib/api';

export const Route = createFileRoute('/_authenticated/payors/$payorId')({
  // Accept ?name=… (pre-fill legalName on create) and ?returnTo=… (where
  // to bounce back to after save — used by the "+ Add new payor" flow
  // from /deals/new and the deal-edit form). Annotated fully-optional so
  // existing Links to /payors/$payorId keep typechecking.
  validateSearch: (search: Record<string, unknown>): { name?: string; returnTo?: string } => ({
    name: typeof search.name === 'string' ? search.name : undefined,
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
  }),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: PayorDetail,
});

type Affiliation = 'unknown' | 'associated' | 'non_associated';

interface FormState {
  legalName: string;
  displayName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  ein: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  industry: string;
  affiliation: Affiliation;
}

const EMPTY: FormState = {
  legalName: '',
  displayName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  website: '',
  ein: '',
  addressLine1: '',
  city: '',
  state: '',
  postalCode: '',
  industry: '',
  affiliation: 'unknown',
};

function PayorDetail() {
  const { payorId } = Route.useParams();
  const { name: nameFromUrl, returnTo } = Route.useSearch();
  const isNew = payorId === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Skip the query entirely when creating — TanStack Query treats `id: 'new'`
  // as a real key otherwise and the server will 404.
  const { data: payor, isLoading } = useQuery({
    ...api.payor.get.queryOptions({ input: { id: payorId } }),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(() =>
    // Pre-fill the legal name if the typeahead handed it to us via ?name=.
    isNew && nameFromUrl ? { ...EMPTY, legalName: nameFromUrl } : EMPTY,
  );
  const [saving, setSaving] = useState(false);

  // Hydrate form when the loaded payor lands.
  useEffect(() => {
    if (!payor || isNew) return;
    setForm({
      legalName: payor.legalName ?? '',
      displayName: payor.displayName ?? '',
      contactName: payor.contactName ?? '',
      contactEmail: payor.contactEmail ?? '',
      contactPhone: payor.contactPhone ?? '',
      website: payor.website ?? '',
      ein: payor.ein ?? '',
      addressLine1: payor.addressLine1 ?? '',
      city: payor.city ?? '',
      state: payor.state ?? '',
      postalCode: payor.postalCode ?? '',
      industry: payor.industry ?? '',
      affiliation: (payor.affiliation as Affiliation) ?? 'unknown',
    });
  }, [payor, isNew]);

  const revalidate = useMutation({
    mutationFn: async () => {
      if (isNew) throw new Error('Save the payor before re-validating');
      return client.payor.revalidate({ id: payorId });
    },
    onSuccess: (row) => {
      queryClient.setQueryData(api.payor.get.queryKey({ input: { id: payorId } }), row);
      // Re-validation can flip validationStatus, which feeds the NIL Go
      // readiness check on every attached deal — bust deal queries too.
      queryClient.invalidateQueries({
        predicate: (q) => {
          const path = q.queryKey?.[0];
          return Array.isArray(path) && path[0] === 'deal';
        },
      });
      toast.success('Validation re-run');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed'),
  });

  async function handleSave() {
    setSaving(true);
    try {
      if (isNew) {
        if (form.legalName.trim().length < 2) {
          toast.error('Legal name is required');
          setSaving(false);
          return;
        }
        const created = await client.payor.create({
          legalName: form.legalName,
          displayName: form.displayName,
          contactName: form.contactName,
          contactEmail: form.contactEmail,
          contactPhone: form.contactPhone,
          website: form.website,
          ein: form.ein,
          addressLine1: form.addressLine1,
          city: form.city,
          state: form.state,
          postalCode: form.postalCode,
          country: 'US',
          industry: form.industry,
          affiliation: form.affiliation,
        });
        toast.success('Payor added');
        queryClient.invalidateQueries({
          // oRPC v1.14+ queryKey shape: `[ ['payor', 'list'], { input, type } ]`
          // (the first element is the path array, NOT a string). So compare
          // `queryKey[0]?.[0]` / `queryKey[0]?.[1]` — comparing `queryKey[0]`
          // to a string is always false and silently skips invalidation.
          predicate: (q) => {
            const path = q.queryKey?.[0];
            return Array.isArray(path) && path[0] === 'payor' && path[1] === 'list';
          },
        });
        // Return to whoever sent us here ("+ Add new payor" from the
        // deal flow). Whitelist the destination so we can't be tricked
        // into an open redirect.
        if (returnTo === '/deals/new') {
          navigate({
            to: '/deals/new',
            search: { payorId: created.id },
          });
        } else {
          navigate({ to: '/payors/$payorId', params: { payorId: created.id } });
        }
      } else {
        const updated = await client.payor.update({
          id: payorId,
          legalName: form.legalName,
          displayName: form.displayName,
          contactName: form.contactName,
          contactEmail: form.contactEmail,
          contactPhone: form.contactPhone,
          website: form.website,
          ein: form.ein,
          addressLine1: form.addressLine1,
          city: form.city,
          state: form.state,
          postalCode: form.postalCode,
          industry: form.industry,
        });
        if (payor && form.affiliation !== payor.affiliation) {
          await client.payor.setAffiliation({ id: payorId, affiliation: form.affiliation });
        }
        queryClient.setQueryData(api.payor.get.queryKey({ input: { id: payorId } }), updated);
        queryClient.invalidateQueries({
          predicate: (q) => {
            const path = q.queryKey?.[0];
            if (!Array.isArray(path)) return false;
            // Refresh the payor list (search typeahead, /payors index).
            if (path[0] === 'payor' && path[1] === 'list') return true;
            // ALSO bust every deal-namespaced query — deal.get embeds the
            // payor row (US-033 edit form depends on it), so a payor edit
            // must propagate to any open deal page that has this payor
            // attached. Same applies to deal.list (renders brandName) and
            // the dashboard.
            if (path[0] === 'deal') return true;
            return false;
          },
        });
        toast.success('Saved');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (!isNew && isLoading) {
    return (
      <div className="max-w-3xl animate-pulse space-y-4">
        <div className="h-8 bg-muted rounded w-64" />
        <div className="h-64 bg-muted rounded" />
      </div>
    );
  }

  const validationStatus =
    (payor?.validationStatus as ValidationBadgeStatus | undefined) ?? 'unverified';

  return (
    <PageTransition>
      <div className="max-w-3xl space-y-6">
        <div className="flex items-center justify-between gap-3">
          <Link
            to="/payors"
            search={{ page: 1 }}
            className="text-sm text-muted-foreground hover:underline"
          >
            ← All payors
          </Link>
          {!isNew && (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-3">
                <ValidationBadge
                  status={validationStatus}
                  title={friendlyValidationMessage(validationStatus)}
                />
                <Button
                  variant="outline"
                  onClick={() => revalidate.mutate()}
                  disabled={revalidate.isPending}
                >
                  {revalidate.isPending ? 'Re-validating…' : 'Re-validate'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground max-w-sm text-right">
                {friendlyValidationMessage(validationStatus)}
              </p>
            </div>
          )}
        </div>

        {!isNew && <YtdExposurePanel payorId={payorId} />}

        <Card>
          <CardHeader>
            <CardTitle>{isNew ? 'New payor' : payor?.legalName}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label="Legal name *"
              hint="As it appears on a W-9 or invoice"
              value={form.legalName}
              onChange={(v) => setForm({ ...form, legalName: v })}
              placeholder="Acme Marketing LLC"
            />
            <Field
              label="d/b/a or display name"
              value={form.displayName}
              onChange={(v) => setForm({ ...form, displayName: v })}
              placeholder="Acme"
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Contact name"
                value={form.contactName}
                onChange={(v) => setForm({ ...form, contactName: v })}
              />
              <Field
                label="Email"
                value={form.contactEmail}
                onChange={(v) => setForm({ ...form, contactEmail: v })}
                type="email"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Phone"
                value={form.contactPhone}
                onChange={(v) => setForm({ ...form, contactPhone: v })}
              />
              <Field
                label="Website"
                value={form.website}
                onChange={(v) => setForm({ ...form, website: v })}
                placeholder="https://"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="EIN (optional)"
                hint="9 digits. Used to validate against the IRS TIN database."
                value={form.ein}
                onChange={(v) => setForm({ ...form, ein: v })}
                placeholder="XX-XXXXXXX"
              />
              <Field
                label="Industry"
                value={form.industry}
                onChange={(v) => setForm({ ...form, industry: v })}
                placeholder="e.g., Apparel"
              />
            </div>
            <Field
              label="Address"
              value={form.addressLine1}
              onChange={(v) => setForm({ ...form, addressLine1: v })}
            />
            <div className="grid sm:grid-cols-3 gap-4">
              <Field
                label="City"
                value={form.city}
                onChange={(v) => setForm({ ...form, city: v })}
              />
              <Field
                label="State"
                value={form.state}
                onChange={(v) => setForm({ ...form, state: v.toUpperCase() })}
                placeholder="CA"
              />
              <Field
                label="ZIP"
                value={form.postalCode}
                onChange={(v) => setForm({ ...form, postalCode: v })}
              />
            </div>

            <div className="space-y-1">
              <Label>Is this person or business connected to your school?</Label>
              <p className="text-xs text-muted-foreground">
                A booster, donor, alumni-owned business, or a relative of someone at the school
                counts as connected. If you're not sure, leave it as "Not sure" — your school can
                help classify it later.
              </p>
              <Select
                value={form.affiliation}
                onValueChange={(v) => setForm({ ...form, affiliation: v as Affiliation })}
              >
                <SelectTrigger className="w-full mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">Not sure</SelectItem>
                  <SelectItem value="non_associated">No — independent business</SelectItem>
                  <SelectItem value="associated">Yes — school-connected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Link to="/payors" search={{ page: 1 }}>
                <Button variant="outline">Cancel</Button>
              </Link>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : isNew ? 'Add payor' : 'Save changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageTransition>
  );
}

/**
 * Athlete-facing explanation for each validation status.
 *
 * The raw `validationDetails.reason` from the validation provider is meant
 * for engineers + audit logs ("Mock match against simulated watchlist",
 * "Matched OFAC SDN list", etc.) and is intentionally NOT shown directly
 * to users. The UI renders this short, plain-English explanation instead.
 */
function friendlyValidationMessage(status: ValidationBadgeStatus): string {
  switch (status) {
    case 'verified':
      return 'Confirmed against public business registries.';
    case 'flagged':
      return 'This business showed up on a sanctions or watchlist check. Review carefully before accepting payment — your school compliance office can help if you have questions. You can still use this payor.';
    case 'unable_to_verify':
      return "We couldn't confirm this business in public registries. That's not unusual for small businesses, individuals paying you directly, or new entities. You can still use this payor.";
    case 'validating':
      return 'Checking this payor against business registries…';
    default:
      return "Validation hasn't run yet. Click Re-validate to check.";
  }
}

function Field({
  label,
  hint,
  value,
  onChange,
  placeholder,
  type,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
      />
    </div>
  );
}

/**
 * US-033.3 — Year-to-date exposure card for this payor.
 *
 * Shows total cash-equivalent the signed-in athlete has accumulated with
 * this payor in the current tax year + a progress bar to the $600 NIL Go
 * threshold. Lists every contributing deal in date order so the athlete
 * can spot which earlier deals tipped them over.
 *
 * Skipped for `new` payors (no exposure to compute yet) — gated by the
 * caller.
 */
function YtdExposurePanel({ payorId }: { payorId: string }) {
  const { data, isLoading } = useQuery(
    api.payor.ytdExposure.queryOptions({ input: { payorId } }),
  );
  if (isLoading || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>YTD with this payor</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-6 bg-muted rounded animate-pulse w-48" />
        </CardContent>
      </Card>
    );
  }
  const pct = Math.min(100, Math.round((data.total / data.thresholdUsd) * 100));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
          <span>YTD with this payor ({data.taxYear})</span>
          <span className={data.overThreshold ? 'text-amber-700 text-base font-semibold' : 'text-base font-semibold'}>
            ${data.total.toLocaleString()} / ${data.thresholdUsd}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden" aria-hidden>
          <div
            className={data.overThreshold ? 'h-full bg-amber-500' : 'h-full bg-primary'}
            style={{ width: `${pct}%` }}
          />
        </div>
        {data.overThreshold ? (
          <p className="text-sm text-amber-900">
            Crossed the NIL Go reporting threshold. All deals with this payor this year are
            reportable — including ones below $600 individually.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Under the NIL Go reporting threshold. We'll flag when a new deal pushes you over.
          </p>
        )}
        {data.deals.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              {data.deals.length} contributing deal{data.deals.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-2 space-y-1">
              {data.deals.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3">
                  <Link
                    to="/deals/$dealId"
                    params={{ dealId: d.id }}
                    className="truncate underline hover:no-underline"
                  >
                    {d.title}
                  </Link>
                  <span className="text-muted-foreground whitespace-nowrap">
                    ${d.cashEquivalent.toLocaleString()} · {d.anchorDate.slice(0, 10)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
